import type { ComponentProps, FC, ReactNode } from 'react'

import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'soft' | 'danger' | 'danger-soft' | 'success'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'icon-sm' | 'icon' | 'icon-lg'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-sm hover:brightness-[0.93] active:brightness-[0.88]',
    secondary: 'border border-border bg-card text-foreground shadow-card hover:bg-slate-50 active:bg-slate-100',
    outline: 'border border-input bg-transparent text-foreground hover:bg-card active:bg-slate-50',
    ghost: 'text-foreground hover:bg-slate-900/[0.05] active:bg-slate-900/[0.09]',
    soft: 'bg-accent text-accent-foreground hover:brightness-[0.97] active:brightness-[0.94]',
    danger: 'bg-destructive text-white shadow-sm hover:brightness-[0.93] active:brightness-[0.88]',
    'danger-soft': 'bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-100',
    success: 'bg-emerald-700 text-white shadow-sm hover:brightness-[0.93] active:brightness-[0.88]'
}

const SIZES: Record<ButtonSize, string> = {
    xs: 'h-8 gap-1 rounded-md px-2.5 text-xs [&_svg]:size-3.5',
    sm: 'h-9 gap-1.5 rounded-md px-3 text-[13px] [&_svg]:size-4',
    md: 'h-11 gap-2 rounded-lg px-4 text-sm [&_svg]:size-[18px]',
    lg: 'h-12 gap-2 rounded-lg px-5 text-[15px] [&_svg]:size-5',
    xl: 'h-14 gap-2.5 rounded-xl px-6 text-base [&_svg]:size-5',
    'icon-sm': 'size-9 rounded-md [&_svg]:size-[18px]',
    icon: 'size-11 rounded-lg [&_svg]:size-5',
    'icon-lg': 'size-12 rounded-xl [&_svg]:size-5'
}

export const buttonClasses = ({
    variant = 'primary',
    size = 'md',
    className
}: {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
} = {}): string => cn(
    'inline-flex shrink-0 select-none items-center justify-center font-semibold whitespace-nowrap transition-[background-color,filter,box-shadow,transform] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45 [&_svg]:shrink-0',
    VARIANTS[variant],
    SIZES[size],
    className
)

interface ButtonProps extends ComponentProps<'button'> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    children?: ReactNode
}

export const Button: FC<ButtonProps> = ({
    variant,
    size,
    loading = false,
    className,
    children,
    disabled,
    type = 'button',
    ...props
}): ReactNode => {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={buttonClasses({ variant, size, className })}
            {...props}
        >
            {loading && <LoaderCircle className='animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}
